import React, { useState, useRef } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { Field, Form } from "react-final-form";
import { HiOutlineArrowLeft } from "@react-icons/all-files/hi/HiOutlineArrowLeft";
import { candidateSelfApply as candidateSelfApplyApi } from "../api";
import {
  TextInput,
  onSubmitHelper,
  SingleFileInput,
  DropdownList,
  Textarea
} from "../../libraries/nodejs-manager/src/final-form/index";
import { FormErrors } from "../../libraries/nodejs-manager/src/manager";
import { OnChange } from "react-final-form-listeners";
import Logo from "./job_description/Logo";
import HiringFooter from "./HiringFooter";
import CheckboxList from "../../libraries/nodejs-manager/src/final-form/CheckboxList";
import RadioList from "../../libraries/nodejs-manager/src/final-form/RadioList";
import TwoButtons from "../../libraries/nodejs-manager/src/final-form/TwoButtons";
import { FaAsterisk } from "@react-icons/all-files/fa/FaAsterisk";
import axios from "axios";
import { LoadingSpinner } from "../common/Loading";

const ApplicationForm = ({
  slug,
  jobId,
  baseUrl,
  logo,
  name,
  url,
  job,
  surveyQuestions,
  preInterviewQuestions,
  surveyOptions,
  captchaSiteKey,
  colorHeaderBg,
  colorHeaderText,
  colorButtonBg,
  colorButtonText,
  allSources,
  employeeOptionName
}) => {
  const [submitted, setSubmitted] = useState(false);
  const [questionsWithOtherVisible, setOtherVisible] = useState([]);
  const [showQuestionTwo, toggleQuestionTwo] = useState(false);
  const [showQuestionThree, toggleQuestionThree] = useState(false);
  const [selectedSource, setSelectedSource] = useState(null);
  const captchaToken = useRef();
  const [resumeProcessing, setResumeProcessing] = useState(false);

  const {
    resume_required: resumeRequired,
    cover_letter_display: coverLetterDisplay,
    cover_letter_required: coverLetterRequired,
    location_display: locationDisplay,
    location_required: locationRequired,
    authorized_to_work_display: authorizedToWorkDisplay,
    authorized_to_work_required: authorizedToWorkRequired,
    seeking_sponsorship_display: seekingSponsorshipDisplay,
    seeking_sponsorship_required: seekingSponsorshipRequired,
    application_message_display: applicationMessageDisplay,
    application_message_required: applicationMessageRequired,
    source_display: sourceDisplay,
    source_required: sourceRequired,
    url_display: urlDisplay,
    url_required: urlRequired,
    url_label: urlLabel
  } = job;

  // mandatory and optional fields
  let validationRules = {
    email: { presence: true, email: true },
    name: { presence: true },
    phone: { presence: true }
  };
  if (locationDisplay && locationRequired)
    validationRules["location"] = { presence: true };
  if (authorizedToWorkDisplay && authorizedToWorkRequired)
    validationRules["authorized_to_work"] = { presence: true };
  if (seekingSponsorshipDisplay && seekingSponsorshipRequired)
    validationRules["seeking_sponsorship"] = { presence: true };
  if (applicationMessageDisplay && applicationMessageRequired)
    validationRules["application_message"] = { presence: true };
  if (sourceDisplay && sourceRequired)
    validationRules["source"] = { presence: true };
  if (resumeRequired) validationRules["resume_file"] = { presence: true };
  if (coverLetterDisplay && coverLetterRequired)
    validationRules["cover_letter_file"] = { presence: true };
  if (urlDisplay && urlRequired)
    validationRules["website__0"] = { presence: true };

  const onSubmit = onSubmitHelper(
    validationRules,
    candidateSelfApplyApi(slug, jobId),
    (payload = {}) => {
      if (payload && payload?.redirect == true) {
        window.location.href = `${baseUrl}/${jobId}`;
      }
      setSubmitted(true);
    }
  );

  const handleCaptchaSuccess = (token) => {
    captchaToken.current = token;
  };

  const inputClasses =
    "w-full px-3 py-3 placeholder-gray-400 border border-gray-400 rounded-md focus:outline-none focus:ring focus:ring-gray-100 focus:border-gray-400";

  const labelClasses =
    "block mb-2 text-lg font-medium text-gray-700 flex items-center";

  return (
    <>
      <div
        className="pt-4 pb-0 lg:pb-8"
        style={{ backgroundColor: colorHeaderBg }}
      >
        <div className="container">
          <div className="flex items-top">
            <div className="mb-4 grow">
              <Logo
                logo={logo}
                name={name}
                colorHeaderText={colorHeaderText}
                url={url}
              />
            </div>
            <div className="mb-4 shrink-0">
              <a
                href={`${baseUrl}/${job["id"]}`}
                className="text-xs font-medium underline xl:text-base"
                style={{ color: colorHeaderText }}
              >
                <HiOutlineArrowLeft className="inline-flex mr-2" />
                Back
              </a>
            </div>
          </div>
          <h1
            className="text-2xl font-semibold md:text-3xl pt-2 md:pt-0 grow"
            style={{ color: colorHeaderText }}
          >
            {job["name"]}
          </h1>
        </div>
      </div>

      <div className="container">
        <div className="flex md:space-x-4 xl:space-x-8">
          {submitted === false && (
            <section className="py-4 px-2 relative max-w-4xl mx-auto xl:px-8">
              <Form
                mutators={{
                  setValue: ([field, value], state, { changeValue }) => {
                    changeValue(state, field, () => value);
                  }
                }}
                initialValues={{ captcha_token: captchaToken }}
                onSubmit={onSubmit}
                render={({
                  handleSubmit,
                  submitError,
                  touched,
                  submitting,
                  form
                }) => {
                  return (
                    <form
                      onSubmit={handleSubmit}
                      method="post"
                      className={`${submitting === true ? "bg-gray-200" : ""}`}
                    >
                      <FormErrors
                        submitError={submitError}
                        touched={touched}
                        scrollContainer={false}
                      />

                      <div>
                        <label htmlFor="resume_file" className={labelClasses}>
                          Resume{" "}
                          {resumeRequired && (
                            <span className="font-normal">
                              <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                            </span>
                          )}
                        </label>
                        <p className="text-base mb-2 text-gray-500">
                          Please upload your resume from your device.
                        </p>
                        <section
                          className={`border-dashed border-2 border-gray-300 py-5 relative max-w-7xl mx-auto px-6 xl:px-8 rounded-lg mb-8 ${
                            resumeProcessing ? "bg-amber-50" : ""
                          }`}
                        >
                          {!resumeProcessing && (
                            <Field
                              name="resume_file"
                              id="resume_file"
                              value={null}
                              accept="application/*"
                              multiple={false}
                              action="Upload Resume"
                              component={SingleFileInput}
                              startCallbackFn={() => {
                                setResumeProcessing(true);
                              }}
                              callbackFn={({ url }) => {
                                const {
                                  mutators: { setValue }
                                } = form;

                                axios
                                  .post("/api/resume", { url })
                                  .then(({ data: { payload = {} } }) => {
                                    setResumeProcessing(false);

                                    const email = payload?.email;
                                    const name = payload?.name;
                                    const phone = payload?.phone;
                                    const resumeParsedAffinda =
                                      payload?.resumeParsedAffinda;

                                    if (name) setValue("name", name.trim());
                                    if (email)
                                      setValue(
                                        "email",
                                        email.trim().toLowerCase()
                                      );
                                    if (phone) setValue("phone", phone.trim());
                                    if (resumeParsedAffinda)
                                      setValue(
                                        "resume_parsed_affinda",
                                        resumeParsedAffinda
                                      );
                                  });
                              }}
                              acl="bucket-owner-full-control"
                              directory="resumes"
                              noPreview={true}
                              tabIndex={0}
                              style={{
                                backgroundColor: colorButtonBg,
                                color: colorButtonText,
                                fontSize: "1rem",
                                padding: "0.75rem 1.25rem"
                              }}
                            />
                          )}
                          {resumeProcessing && (
                            <div className="flex items-center gap-x-4">
                              <LoadingSpinner />
                              <div className="font-semibold">
                                One moment, processing resume
                              </div>
                            </div>
                          )}
                          <Field
                            type="hidden"
                            component={TextInput}
                            name="resume_parsed_affinda"
                            id="resume_parsed_affinda"
                            required={false}
                          />
                        </section>

                        {coverLetterDisplay && (
                          <label htmlFor="resume_file" className={labelClasses}>
                            Cover Letter
                            {coverLetterRequired && (
                              <span className="font-normal">
                                <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                              </span>
                            )}
                          </label>
                        )}

                        {coverLetterDisplay && (
                          <section className="border-dashed border-2 border-gray-300 py-5 relative max-w-7xl mx-auto px-6 xl:px-8 rounded-lg mb-8">
                            <Field
                              name="cover_letter_file"
                              id="cover_letter_file"
                              value={null}
                              accept="application/*"
                              multiple={false}
                              action="Upload Cover Letter"
                              component={SingleFileInput}
                              acl="bucket-owner-full-control"
                              directory="resumes"
                              noPreview={true}
                              tabIndex={0}
                              style={{
                                backgroundColor: colorButtonBg,
                                color: colorButtonText,
                                fontSize: "1rem",
                                padding: "0.75rem 1.25rem"
                              }}
                            />
                          </section>
                        )}

                        {/* <h2 className="mb-2 text-lg font-semibold xl:mb-4 xl:text-2xl">
                          Personal Details
                        </h2> */}

                        <div className="mb-10">
                          <label htmlFor="name" className={labelClasses}>
                            Full Name{" "}
                            <span className="font-normal">
                              <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                            </span>
                          </label>

                          <Field
                            tabIndex={0}
                            component={TextInput}
                            name="name"
                            id="name"
                            placeholder="Your Name"
                            required={true}
                            className={inputClasses}
                          />
                        </div>

                        <div className="mb-10">
                          <label htmlFor="pronoun" className={labelClasses}>
                            Pronouns
                          </label>

                          <p className="text-base mb-2 text-gray-500">
                            We want to make sure we’re referring to you properly
                            in your notes and interview communication so please
                            tell us, what are your pronouns?
                          </p>

                          <Field
                            tabIndex={0}
                            component={TextInput}
                            name="pronoun"
                            id="pronoun"
                            placeholder=""
                            required={false}
                            className={inputClasses}
                          />
                          <div className="text-sm mt-2 text-gray-500">
                            Examples: he/him/his, they/them/theirs
                          </div>
                        </div>

                        <div className="mb-10">
                          <label htmlFor="email" className={labelClasses}>
                            Email Address{" "}
                            <span className="font-normal">
                              <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                            </span>
                          </label>

                          <Field
                            tabIndex={0}
                            component={TextInput}
                            name="email"
                            id="email"
                            placeholder="Email"
                            required={true}
                            className={inputClasses}
                          />
                        </div>

                        <div className="mb-10">
                          <label htmlFor="phone" className={labelClasses}>
                            Phone Number{" "}
                            <span className="font-normal">
                              <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                            </span>
                          </label>

                          <Field
                            tabIndex={0}
                            component={TextInput}
                            name="phone"
                            id="phone"
                            placeholder="(555) 666-7777"
                            required={true}
                            className={inputClasses}
                          />
                        </div>

                        {locationDisplay && (
                          <div className="mb-10">
                            <label htmlFor="location" className={labelClasses}>
                              Location{" "}
                              {locationRequired && (
                                <span className="font-normal">
                                  <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                </span>
                              )}
                            </label>

                            <Field
                              tabIndex={0}
                              component={TextInput}
                              name="location"
                              id="location"
                              placeholder="City, State"
                              required={locationRequired}
                              className={inputClasses}
                            />
                          </div>
                        )}

                        {authorizedToWorkDisplay && (
                          <div className="mb-10">
                            <label
                              htmlFor="authorized_to_work"
                              className={labelClasses}
                            >
                              Are you authorized to work in the country in which
                              this job is offered?{" "}
                              {authorizedToWorkRequired && (
                                <span className="font-normal">
                                  <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                </span>
                              )}
                            </label>

                            <Field
                              tabIndex={0}
                              component={TwoButtons}
                              name="authorized_to_work"
                              id="authorized_to_work"
                              options={[
                                { label: "Yes", value: true },
                                { label: "No", value: false }
                              ]}
                              required={authorizedToWorkRequired}
                              className={inputClasses}
                            />
                          </div>
                        )}

                        {seekingSponsorshipDisplay && (
                          <div className="mb-10">
                            <label
                              htmlFor="seeking_sponsorship"
                              className={labelClasses}
                            >
                              Will you now or in the future require sponsorship
                              to continue or extend your current work
                              authorization status?{" "}
                              {seekingSponsorshipRequired && (
                                <span className="font-normal">
                                  <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                </span>
                              )}
                            </label>

                            <Field
                              tabIndex={0}
                              component={TwoButtons}
                              name="seeking_sponsorship"
                              id="seeking_sponsorship"
                              options={[
                                { label: "Yes", value: true },
                                { label: "No", value: false }
                              ]}
                              required={seekingSponsorshipRequired}
                              className={inputClasses}
                            />
                          </div>
                        )}

                        {sourceDisplay && (
                          <div className="mb-10">
                            <label htmlFor="source" className={labelClasses}>
                              How did you hear about his role?{" "}
                              {sourceRequired && (
                                <span className="font-normal">
                                  <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                </span>
                              )}
                            </label>

                            <Field
                              tabIndex={0}
                              component={DropdownList}
                              name="source"
                              id="source"
                              options={allSources.map((source) => ({
                                label: source,
                                value: source
                              }))}
                              required={sourceRequired}
                              classNames={{
                                control: () =>
                                  inputClasses.replace("px-3 py-3", "px-2 py-2")
                              }}
                            />
                            {selectedSource === "Other" && (
                              <div className="mt-4">
                                <label
                                  htmlFor="source_other"
                                  className={labelClasses}
                                >
                                  <span className="font-normal">
                                    Please enter your source below{" "}
                                    {sourceRequired && (
                                      <span className="font-normal">
                                        <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                      </span>
                                    )}
                                  </span>
                                </label>

                                <Field
                                  tabIndex={0}
                                  component={TextInput}
                                  name="source_other"
                                  id="source_other"
                                  placeholder="Other option"
                                  required={true}
                                  className={inputClasses}
                                />
                              </div>
                            )}
                            {selectedSource === employeeOptionName && (
                              <div className="mt-4">
                                <label
                                  htmlFor="source_employee"
                                  className={labelClasses}
                                >
                                  <span className="font-normal">
                                    Which employee was it?{" "}
                                    {sourceRequired && (
                                      <span className="font-normal">
                                        <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                      </span>
                                    )}
                                  </span>
                                </label>

                                <Field
                                  tabIndex={0}
                                  component={TextInput}
                                  name="source_employee"
                                  id="source_employee"
                                  placeholder="Employee name"
                                  required={sourceRequired}
                                  className={inputClasses}
                                />
                              </div>
                            )}
                            <OnChange name="source">
                              {(value) => {
                                setSelectedSource(value);
                              }}
                            </OnChange>
                          </div>
                        )}

                        {applicationMessageDisplay && (
                          <div className="mb-4">
                            <label
                              htmlFor="application_message"
                              className={labelClasses}
                            >
                              Is there anything else you would like to share
                              with us?
                              {applicationMessageRequired && (
                                <span className="font-normal">
                                  <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                </span>
                              )}
                            </label>

                            <Field
                              tabIndex={0}
                              component={Textarea}
                              name="application_message"
                              id="application_message"
                              placeholder="Notes"
                              required={false}
                              className={inputClasses}
                              style={{ height: "120px" }}
                            />
                          </div>
                        )}
                      </div>

                      {urlDisplay && (
                        <div>
                          <div className="mb-10">
                            <label
                              htmlFor="website__0"
                              className={labelClasses}
                            >
                              {urlLabel}
                              {urlRequired && (
                                <span className="font-normal">
                                  <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                </span>
                              )}
                            </label>

                            <Field
                              tabIndex={0}
                              component={TextInput}
                              name="website__0"
                              id="website__0"
                              placeholder="https://somesite.com"
                              required={false}
                              className={inputClasses}
                            />

                            <OnChange name={`website__0`}>
                              {(value) => {
                                if (value.trim().length > 0) {
                                  toggleQuestionTwo(true);
                                } else {
                                  toggleQuestionTwo(false);
                                }
                              }}
                            </OnChange>

                            {showQuestionTwo && (
                              <div className="mt-4">
                                <Field
                                  tabIndex={0}
                                  component={TextInput}
                                  name="website__1"
                                  id="website__1"
                                  placeholder="https://somesite.com"
                                  required={false}
                                  className={inputClasses}
                                />
                              </div>
                            )}

                            <OnChange name={`website__1`}>
                              {(value) => {
                                if (value.trim().length > 0) {
                                  toggleQuestionThree(true);
                                } else {
                                  toggleQuestionThree(false);
                                }
                              }}
                            </OnChange>

                            {showQuestionThree && (
                              <div className="mt-4">
                                <Field
                                  tabIndex={0}
                                  component={TextInput}
                                  name="website__2"
                                  id="website__2"
                                  placeholder="https://somesite.com"
                                  required={false}
                                  className={inputClasses}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                      <div>
                        {preInterviewQuestions.map(
                          ({ id, question_text: question, options }) => {
                            options = options ? options.trim().split("\n") : [];
                            return (
                              <div className="mb-10" key={id}>
                                <label
                                  htmlFor={`custom__${id}`}
                                  className={labelClasses}
                                >
                                  {question}
                                  <span className="font-normal">
                                    <FaAsterisk className="ml-1 inline-block w-3 h-3 text-red-400 self-center" />
                                  </span>
                                </label>
                                <Field
                                  tabIndex={0}
                                  component={
                                    options.length > 0 ? DropdownList : Textarea
                                  }
                                  options={options.map((option) => ({
                                    label: option,
                                    value: option
                                  }))}
                                  name={`custom__${id}`}
                                  id={`custom__${id}`}
                                  placeholder=""
                                  required={true}
                                  className={inputClasses}
                                  style={{ height: "90px" }}
                                />
                              </div>
                            );
                          }
                        )}
                      </div>

                      <div className="bg-slate-100 p-4">
                        <h2 className="mb-2 text-lg font-semibold xl:mb-4 xl:text-2xl">
                          Equal Opportunity
                        </h2>

                        <p className="text-base mb-4">
                          In an effort to promote equal opportunities and
                          reinforce hiring practices at our organization we have
                          included below some{" "}
                          <span className="font-bold">optional</span>{" "}
                          demographic questions. Your responses, or your choice
                          not to respond, is{" "}
                          <span className="font-bold">entirely anonymous</span>{" "}
                          and will not be associated with your application.
                        </p>

                        {surveyQuestions.map(
                          ({
                            id,
                            question,
                            multiple_choice: multipleChoice,
                            description,
                            other_offered: otherOffered
                          }) => (
                            <div key={id} className="mb-10 last:mb-0">
                              <label
                                htmlFor={`question__${id}`}
                                className={labelClasses}
                              >
                                {question}
                              </label>
                              <Field
                                tabIndex={0}
                                name={`question__${id}`}
                                id={`question__${id}`}
                                component={
                                  multipleChoice === true
                                    ? CheckboxList
                                    : RadioList
                                }
                                options={surveyOptions
                                  .filter(
                                    ({ demographic_question_id }) =>
                                      demographic_question_id === id
                                  )
                                  .map(({ id: optionId, option }) => ({
                                    label: option,
                                    value: optionId
                                  }))
                                  .concat(
                                    otherOffered
                                      ? [
                                          {
                                            value: "__OTHER__",
                                            label: "Prefer to self-describe"
                                          }
                                        ]
                                      : []
                                  )}
                                isMulti={multipleChoice === true ? true : false}
                                className={
                                  multipleChoice
                                    ? "text-base w-full px-3 py-3"
                                    : inputClasses
                                }
                              />
                              {description && (
                                <div
                                  className="text-sm mt-2 text-gray-500"
                                  dangerouslySetInnerHTML={{
                                    __html: description
                                  }}
                                />
                              )}
                              {questionsWithOtherVisible.indexOf(id) !== -1 && (
                                <div className="mt-4">
                                  <Field
                                    tabIndex={0}
                                    component={TextInput}
                                    name={`question__${id}__other`}
                                    id={`question__${id}__other`}
                                    placeholder="Additional Option"
                                    required={true}
                                    className={inputClasses}
                                  />
                                </div>
                              )}
                              <OnChange name={`question__${id}`}>
                                {(value) => {
                                  if (Array.isArray(value)) {
                                    if (value.indexOf("__OTHER__") !== -1) {
                                      setOtherVisible(
                                        questionsWithOtherVisible.concat([id])
                                      );
                                    } else {
                                      setOtherVisible(
                                        questionsWithOtherVisible.filter(
                                          (visibleId) => visibleId !== id
                                        )
                                      );
                                    }
                                  } else {
                                    if (value === "__OTHER__") {
                                      setOtherVisible(
                                        questionsWithOtherVisible.concat([id])
                                      );
                                    } else {
                                      setOtherVisible(
                                        questionsWithOtherVisible.filter(
                                          (visibleId) => visibleId !== id
                                        )
                                      );
                                    }
                                  }
                                }}
                              </OnChange>
                            </div>
                          )
                        )}
                      </div>

                      <div className="mb-4 mt-4">
                        <HCaptcha
                          sitekey={captchaSiteKey}
                          onVerify={(token, ekey) =>
                            handleCaptchaSuccess(token, ekey)
                          }
                        />
                      </div>

                      <div className="mt-2">
                        {submitting === false && (
                          <input
                            tabIndex={0}
                            type="submit"
                            className="w-full text-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm cursor-pointer"
                            style={{
                              color: colorButtonText,
                              backgroundColor: colorButtonBg
                            }}
                            value="Submit Application"
                          />
                        )}
                        {submitting === true && (
                          <div className="w-full text-center px-4 py-2 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-400">
                            Submitting Application...
                          </div>
                        )}
                      </div>
                    </form>
                  );
                }}
              />
            </section>
          )}
          {submitted === true && (
            <section className="py-4 relative max-w-4xl mx-auto px-6 xl:px-8">
              <h2 className="mb-4 text-2xl font-semibold xl:mb-6 xl:text-4xl text-center">
                Application Received
              </h2>
            </section>
          )}
        </div>
        <HiringFooter />
      </div>
    </>
  );
};

export default ApplicationForm;
